import React, { useEffect, useState, useRef } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollbarUtils';
import { tapClickProps } from '../utils/tapFix';
import { checkTableAvailability } from '../utils/unionApi';
import { simApi } from '../utils/simApi';
import { checkTableOrderExists } from '../utils/okdcApi';
import reviewNaverIcon from '../assets/icons/review/naver.png';
import reviewIcon from '../assets/icons/admin/review_icon.png';
import adminArrowIcon from '../assets/icons/admin/arrow.png';
import bottomNavBg from '../assets/icons/common/bottomNav.png';

const ReviewBenefits = ({ isOpen, onClose, onConfirm, totalPrice = 36000, tableCode = '001', posType = null, storeId = null, reviewSettings = null, isSimulation = false }) => {
  const [benefits, setBenefits] = useState([]);
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isReviewEnabled, setIsReviewEnabled] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [buttonTextAnimating, setButtonTextAnimating] = useState(false);
  const [previousBenefit, setPreviousBenefit] = useState(null);
  const [headerMenuAnimating, setHeaderMenuAnimating] = useState(false);
  const [previousMenuName, setPreviousMenuName] = useState(null);
  const carouselStartX = useRef(0);
  const [dragOffset, setDragOffset] = useState(0);
  const isDraggingCarousel = useRef(false);
  const [previewIdx, setPreviewIdx] = useState(null);

  // Load menus from props (DB에서 로드된 "네이버 리뷰" 카테고리 메뉴들)
  useEffect(() => {
    if (reviewSettings) {
      // Props로 받은 설정 사용 (DB에서 로드된 값)
      console.log('[ReviewBenefits] ✅ Props에서 리뷰 설정 로드:', reviewSettings);
      setIsReviewEnabled(reviewSettings.isEnabled || false);

      if (reviewSettings.menus && reviewSettings.menus.length > 0) {
        // "네이버 리뷰" 카테고리 메뉴들을 혜택 목록으로 변환
        const loadedBenefits = reviewSettings.menus
          .filter(menu => !menu.is_soldout)
          .map((menu, index) => ({
            id: menu.id,
            text: menu.name,
            menuData: menu
          }));

        setBenefits(loadedBenefits);

        // 첫 번째 메뉴를 기본 선택
        if (loadedBenefits.length > 0) {
          setSelectedBenefit(loadedBenefits[0].id);
        }
      } else {
        setBenefits([]);
        setSelectedBenefit(null);
      }
    } else {
      // Props 없으면 localStorage 폴백 (레거시)
      const savedSettings = localStorage.getItem('reviewSettings');
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          setIsReviewEnabled(settings.isEnabled !== false);

          if (settings.discountItems && settings.discountItems.length > 0) {
            const loadedBenefits = settings.discountItems
              .filter(item => item.enabled !== false)
              .map((item, index) => {
                if (item.type === 'discount') {
                  const discount = parseInt(item.value) || 0;
                  return {
                    id: index + 1,
                    text: `${discount.toLocaleString('ko-KR')}원 즉시 할인`,
                    discount: discount
                  };
                } else {
                  return {
                    id: index + 1,
                    text: item.value || '혜택'
                  };
                }
              });
            setBenefits(loadedBenefits);

            if (loadedBenefits.length > 0) {
              setSelectedBenefit(loadedBenefits[0].id);
            }
          }
        } catch (e) {
          console.error('[ReviewBenefits] ❌ localStorage 로드 실패:', e);
          setIsReviewEnabled(true);
          setBenefits([]);
          setSelectedBenefit(null);
        }
      } else {
        setIsReviewEnabled(true);
        setBenefits([]);
        setSelectedBenefit(null);
      }
    }
    setSettingsLoaded(true);
  }, [reviewSettings, totalPrice]);

  const handleBenefitSelect = (benefitId) => {
    const newBenefit = benefits.find(b => b.id === benefitId);
    const currentBenefit = benefits.find(b => b.id === selectedBenefit);

    if (currentBenefit && newBenefit && currentBenefit.id !== newBenefit.id) {
      // 헤더 메뉴 이름 룰렛 애니메이션
      setPreviousMenuName(currentBenefit.text);
      setHeaderMenuAnimating(true);
      setTimeout(() => {
        setHeaderMenuAnimating(false);
        setPreviousMenuName(null);
      }, 300);

      // 할인 <-> 비할인 전환일 때 버튼 텍스트 애니메이션
      const currentIsDiscount = !!currentBenefit.discount;
      const newIsDiscount = !!newBenefit.discount;

      if (currentIsDiscount !== newIsDiscount) {
        setButtonTextAnimating(true);
        setPreviousBenefit(currentBenefit);

        setTimeout(() => {
          setButtonTextAnimating(false);
          setPreviousBenefit(null);
        }, 300);
      }
    }

    setSelectedBenefit(benefitId);
  };

  const handleReviewOrder = async () => {
    // POS 상태 체크
    try {
      if (posType === 'demo') {
        // 데모: POS 체크 건너뜀
      } else if (posType === 'okdc') {
        // OKDC: 주문 존재 여부 체크
        const { hasOrder } = await checkTableOrderExists(storeId, tableCode);
        if (hasOrder) {
          onClose('orderFailed', '다시 시도해주세요.');
          setTimeout(() => {
            setIsAnimating(false);
            setTimeout(() => {
              setIsVisible(false);
              if (document.body.dataset.scrollY) {
                unlockScroll();
              }
              document.body.classList.remove('modal-open');
            }, 300);
          }, 50);
          return;
        }
      } else if (posType === 'wowpos' || posType === 'metacity' || posType === 'toss') {
        // WowPos/METACITY/토스: 테이블 사용 가능 여부 사전 체크 불필요 — 주문 전송이 처리한다
        // (토스는 서버가 기존 주문 유무를 보고 새 주문/추가 주문을 고른다).
        // 분기가 없으면 유니온 경로로 빠져 /api/union/table/availability 가 500 → 주문이 막힌다.
      } else {
        // Union: 테이블 사용 가능 여부 체크 (시뮬은 simApi)
        const tableStatus = isSimulation
          ? await simApi.checkTableAvailability()
          : await checkTableAvailability(tableCode);
        if (tableStatus.tableUse !== 'N') {
          onClose('orderFailed', '다시 시도해주세요.');
          setTimeout(() => {
            setIsAnimating(false);
            setTimeout(() => {
              setIsVisible(false);
              if (document.body.dataset.scrollY) {
                unlockScroll();
              }
              document.body.classList.remove('modal-open');
            }, 300);
          }, 50);
          return;
        }
      }
    } catch (error) {
      console.error('주문 가능 여부 체크 실패:', error);
      onClose('orderFailed', '다시 시도해주세요.');
      setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          if (document.body.dataset.scrollY) {
            unlockScroll();
          }
          document.body.classList.remove('modal-open');
        }, 300);
      }, 50);
      return;
    }

    if (selectedBenefit) {
      const selectedBenefitData = benefits.find(b => b.id === selectedBenefit);
      // 주문 처리는 부모에서 진행. 부모가 isOpen을 false로 만들면 자연스럽게 닫힘
      onConfirm(true, selectedBenefitData);
    }
  };

  const handleJustOrder = async () => {
    // POS 상태 체크
    try {
      if (posType === 'demo') {
        // 데모: POS 체크 건너뜀
      } else if (posType === 'okdc') {
        // OKDC: 주문 존재 여부 체크
        const { hasOrder } = await checkTableOrderExists(storeId, tableCode);
        if (hasOrder) {
          onClose('orderFailed', '다시 시도해주세요.');
          setTimeout(() => {
            setIsAnimating(false);
            setTimeout(() => {
              setIsVisible(false);
              if (document.body.dataset.scrollY) {
                unlockScroll();
              }
              document.body.classList.remove('modal-open');
            }, 300);
          }, 50);
          return;
        }
      } else if (posType === 'wowpos' || posType === 'metacity' || posType === 'toss') {
        // WowPos/METACITY/토스: 테이블 사용 가능 여부 사전 체크 불필요 — 주문 전송이 처리한다
        // (토스는 서버가 기존 주문 유무를 보고 새 주문/추가 주문을 고른다).
        // 분기가 없으면 유니온 경로로 빠져 /api/union/table/availability 가 500 → 주문이 막힌다.
      } else {
        // Union: 테이블 사용 가능 여부 체크 (시뮬은 simApi)
        const tableStatus = isSimulation
          ? await simApi.checkTableAvailability()
          : await checkTableAvailability(tableCode);
        if (tableStatus.tableUse !== 'N') {
          onClose('orderFailed', '다시 시도해주세요.');
          setTimeout(() => {
            setIsAnimating(false);
            setTimeout(() => {
              setIsVisible(false);
              if (document.body.dataset.scrollY) {
                unlockScroll();
              }
              document.body.classList.remove('modal-open');
            }, 300);
          }, 50);
          return;
        }
      }
    } catch (error) {
      console.error('주문 가능 여부 체크 실패:', error);
      onClose('orderFailed', '다시 시도해주세요.');
      setTimeout(() => {
        setIsAnimating(false);
        setTimeout(() => {
          setIsVisible(false);
          if (document.body.dataset.scrollY) {
            unlockScroll();
          }
          document.body.classList.remove('modal-open');
        }, 300);
      }, 50);
      return;
    }

    // 주문 버튼 클릭시 즉시 닫기 (애니메이션 없음)
    setIsVisible(false);
    setIsAnimating(false);
    // 스크롤이 잠겨있는 경우에만 unlockScroll 호출
    if (document.body.dataset.scrollY) {
      unlockScroll();
    }
    document.body.classList.remove('modal-open');
    onConfirm(false, null);
  };



  // 슬라이드 애니메이션 및 body 스크롤 방지
  useEffect(() => {
    if (isOpen && settingsLoaded) {
      // 설정이 로드된 후 리뷰가 비활성화되어 있으면 바로 닫기
      if (!isReviewEnabled) {
        onConfirm(false, null);
        return;
      }
      
      setIsVisible(true);
      // 스크롤이 이미 잠겨있지 않은 경우에만 lockScroll 호출
      if (!document.body.dataset.scrollY) {
        lockScroll();
      }
      document.body.classList.add('modal-open');
      
      // 열릴 때는 항상 슬라이드인 애니메이션
      setTimeout(() => {
        setIsAnimating(true);
      }, 10);
    } else if (!isOpen && isVisible) {
      // 닫을 때 - 뒤로가기로 닫는 경우에만 여기로 옴
      setIsAnimating(false);
      
      const timer = setTimeout(() => {
        setIsVisible(false);
        // 스크롤이 잠겨있는 경우에만 unlockScroll 호출
        if (document.body.dataset.scrollY) {
          unlockScroll();
        }
        document.body.classList.remove('modal-open');
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isOpen, settingsLoaded, isReviewEnabled, onConfirm]);

  useEffect(() => {
    const scaleUI = () => {
      const wrapper = document.querySelector('.review-benefits-wrapper');
      if (wrapper) {
        const viewportWidth = window.innerWidth;
        if (viewportWidth >= 540) {
          // 넓은 화면(폴드 펼침/태블릿): zoom 확대는 세로(height:100%)까지 늘려
          // 하단(이미지·버튼)이 잘림 → 확대 없이 중앙 정렬 모바일 컬럼으로 표시
          wrapper.style.zoom = '1';
          wrapper.style.width = '420px';
        } else {
          wrapper.style.zoom = Math.min(viewportWidth / 360, 1.5);
          wrapper.style.width = '';
        }
      }
    };
    
    if (isOpen) {
      scaleUI();
      window.addEventListener('resize', scaleUI);
    }
    
    return () => {
      window.removeEventListener('resize', scaleUI);
    };
  }, [isOpen]);

  if ((!isOpen && !isVisible) || (!settingsLoaded && !isReviewEnabled)) return null;

  return (
    <>
      <style jsx="true">{`
        .review-benefits-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: white;
          z-index: 9999;
          overflow: hidden;
          transform: translateX(100%);
          transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .review-benefits-container.open {
          transform: translateX(0);
        }

        .review-benefits-wrapper {
          width: 360px;
          height: 100%;
          margin: 0 auto;
          position: relative;
          display: flex;
          flex-direction: column;
          transform-origin: top center;
          overflow: hidden;
        }
        
        @media screen and (min-width: 361px) {
          .review-benefits-wrapper {
            zoom: calc(100vw / 360);
          }
        }
        
        @media screen and (min-width: 540px) {
          .review-benefits-wrapper {
            zoom: 1;
            width: 420px;
          }
        }
        
        @media screen and (max-width: 360px) {
          .review-benefits-wrapper {
            width: 100vw;
            zoom: calc(100vw / 360);
          }
        }


        /* Design C - 풀스크린 */
        .dc-container {
          height: 100%;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, #eafff3 0%, #fff 50%);
          overflow: hidden;
        }

        .dc-top {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 50px 24px 10px;
          text-align: center;
          overflow-y: auto;
          scrollbar-width: none;
          touch-action: none;
        }

        .dc-top::-webkit-scrollbar {
          display: none;
        }

        .dc-n-logo {
          margin-bottom: 20px;
        }

        .dc-n-logo-img {
          width: 40px;
          height: 40px;
        }

        .dc-headline {
          font-size: 24px;
          font-weight: 800;
          line-height: 30px;
          margin-bottom: 30px;
          color: #1a1a1a;
        }

        .dc-green { color: #03C75A; }

        /* 카카오: 노랑(#fdcd27) 톤으로 통일 (초록 계열만 교체, 회색/음식사진은 유지) */
        .review-benefits-container.kakao-tone .dc-container { background: linear-gradient(180deg, #fffbdf 0%, #fff 50%); }
        .review-benefits-container.kakao-tone .dc-green { color: #007dfc; }
        .review-benefits-container.kakao-tone .dc-reward-no-image { color: #fdcd27; }
        .review-benefits-container.kakao-tone .dc-sparkle { color: #fdcd27; }
        .review-benefits-container.kakao-tone .dc-reward-sublabel { color: #494949; }
        .review-benefits-container.kakao-tone .dc-dot.active { background: #fdcd27; }
        .review-benefits-container.kakao-tone .dc-btn-primary { background: #fdcd27; color: #191919; box-shadow: 0 4px 16px rgba(253, 205, 39, 0.35); }

        .dc-rolling-wrapper {
          display: inline-block;
          position: relative;
          overflow: hidden;
          vertical-align: bottom;
          height: 30px;
          line-height: 30px;
        }

        .dc-rolling-exit {
          display: inline-block;
          position: absolute;
          animation: dcRollOut 0.3s ease forwards;
        }

        .dc-rolling-enter {
          display: inline-block;
          animation: dcRollIn 0.3s ease forwards;
        }

        @keyframes dcRollOut {
          from { transform: translateY(0); opacity: 1; }
          to { transform: translateY(100%); opacity: 0; }
        }

        @keyframes dcRollIn {
          from { transform: translateY(-100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        .dc-desc {
          font-size: 13px;
          font-weight: 600;
          color: #999;
          line-height: 1.5;
          margin-bottom: 4px;
          margin-top: 4px;
        }

        .dc-star-req {
          font-size: 14px;
          font-weight: 600;
          color: #808080;
          line-height: 1.5;
          margin-top: 4px;
        }

        .dc-star-row {
          font-size: 15px;
          letter-spacing: 3px;
          color: #808080;
          margin-top: 3px;
        }

        /* Carousel */
        .dc-carousel {
          width: 100%;
          overflow: visible;
          margin-bottom: 8px;
          padding: 16px 0;
        }

        .dc-carousel-track {
          display: flex;
          gap: 16px;
          padding-left: calc(50% - 93px);
        }

        .dc-reward-card {
          flex-shrink: 0;
          width: 186px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: opacity 0.3s, transform 0.3s;
          opacity: 0.35;
          transform: scale(0.85);
          position: relative;
        }

        .dc-reward-card.active {
          opacity: 1;
          transform: scale(1);
        }

        .dc-reward-main {
          width: 170px;
          height: 170px;
          border-radius: 24px;
          background: #fff;
          box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          overflow: hidden;
        }

        .dc-reward-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .dc-reward-no-image {
          font-size: 40px;
          font-weight: 800;
          color: #03C75A;
          opacity: 0.3;
        }

        .dc-sparkle {
          position: absolute;
          color: #20d470;
          font-size: 14px;
          z-index: 2;
          opacity: 0;
          animation: none;
        }

        .dc-reward-card.active .dc-sparkle {
          animation: sparkle 2s ease-in-out infinite;
        }

        .dc-sparkle:nth-child(1) { top: -8px; right: -12px; animation-delay: 0s; }
        .dc-sparkle:nth-child(2) { top: 10px; left: -16px; animation-delay: 0.7s; }
        .dc-sparkle:nth-child(3) { bottom: -4px; right: -8px; animation-delay: 1.4s; }

        @keyframes sparkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }

        .dc-reward-label {
          text-align: center;
          margin-top: 14px;
          font-size: 16px;
          font-weight: 700;
          color: #1a1a1a;
        }

        .dc-reward-sublabel {
          text-align: center;
          font-size: 16px;
          color: #03C75A;
          font-weight: 800;
          margin-top: 4px;
          margin-bottom: 4px;
        }

        .dc-step-dots {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-bottom: 16px;
        }

        .dc-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ddd;
          transition: all 0.3s;
        }

        .dc-dot.active {
          width: 20px;
          border-radius: 3px;
          background: #03C75A;
        }

        .dc-bottom {
          padding: 16px 20px 16px;
          flex-shrink: 0;
        }

        .dc-btn-primary {
          width: 100%;
          padding: 17px;
          background: #03C75A;
          color: #fff;
          border: none;
          border-radius: 14px;
          font-family: inherit;
          font-size: 16px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 4px 16px rgba(3, 199, 90, 0.3);
          position: relative;
          overflow: hidden;
        }

        .dc-btn-primary::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
          animation: shimmer 3s infinite;
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        .dc-btn-primary:active {
          transform: scale(0.96);
        }

        .dc-btn-skip {
          width: 100%;
          padding: 14px;
          background: transparent;
          color: #999;
          border: none;
          font-family: inherit;
          font-size: 13px;
          cursor: pointer;
          margin-top: 4px;
        }

        .dc-btn-skip:active {
          opacity: 0.6;
        }

        /* 등장 애니메이션 */
        .dc-enter {
          opacity: 0;
          transform: translateY(24px);
          animation: dcEnter 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .dc-enter-1 {
          animation-delay: 0.2s;
          transform: translateY(0) scale(0.5);
          animation-name: dcEnterBounce;
          animation-duration: 0.4s;
          animation-timing-function: ease-out;
        }

        .dc-enter-2 { animation-delay: 0.4s; }
        .dc-enter-3 { animation-delay: 0.6s; animation-name: dcEnterScale; transform: translateY(0) scale(0.85); animation-duration: 0.6s; }
        .dc-enter-4 { animation-delay: 0.75s; }
        .dc-enter-5 { animation-delay: 0.9s; }
        .dc-enter-6 { animation-delay: 1.05s; transform: translateY(30px); }

        @keyframes dcEnter {
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dcEnterBounce {
          0% { opacity: 0; transform: scale(0.3); }
          50% { opacity: 1; transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes dcEnterScale {
          0% { opacity: 0; transform: scale(0.85); }
          100% { opacity: 1; transform: scale(1); }
        }

        .header-menu-name-wrapper {
          display: inline-block;
          position: relative;
          overflow: hidden;
          vertical-align: bottom;
          height: 30px;
        }

        .header-menu-name {
          display: inline-block;
          color: #000000;
        }

        .header-menu-exit {
          position: absolute;
          animation: menuSlideOut 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        .header-menu-enter {
          animation: menuSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
        }

        @keyframes menuSlideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(100%);
            opacity: 0;
          }
        }

        @keyframes menuSlideIn {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .benefit-selection-section {
          margin-top: 21px;
        }


        .discount-info {
          margin-top: 10px;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .original-price {
          font-size: 14px;
          font-weight: 500;
          color: #949494;
          text-decoration: line-through;
        }

        .arrow-icon {
          width: 19px;
          height: 10px;
        }

        .discounted-price {
          font-size: 16px;
          font-weight: bold;
          color: #FDCD27;
        }

        .radio-wrapper {
          position: relative;
          width: 19px;
          height: 19px;
          margin-top: 0;
          flex-shrink: 0;
        }

        .radio-wrapper svg {
          position: absolute;
          top: 0;
          left: 0;
        }

        .radio-wrapper .radio-bg {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .radio-wrapper.selected .radio-bg {
          opacity: 1;
        }

        .radio-wrapper .radio-center {
          opacity: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
          transform-origin: center;
        }

        .radio-wrapper.selected .radio-center {
          opacity: 1;
          animation: radioPopIn 0.2s ease;
        }

        .radio-wrapper .radio-border {
          transition: opacity 0.2s ease;
        }

        .radio-wrapper.selected .radio-border {
          opacity: 0;
        }

        @keyframes radioPopIn {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }

        /* 가격 룰렛 애니메이션 스타일 */
        .price-slot-container {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          overflow: visible;
          height: 19px;
          position: relative;
        }
        
        .digit-container {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          height: 19px;
          min-width: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .digit-container.comma {
          min-width: 3px;
          margin: 0;
        }
        
        .digit {
          display: inline-block;
          line-height: 19px;
          transition: none;
          font-size: 17px;
          font-weight: 800;
          color: #ffffff;
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
        }
        
        .digit-exit {
          position: absolute;
          animation: slideOutBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-exit-up {
          position: absolute;
          animation: slideOutTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter {
          animation: slideInTop 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        .digit-enter-up {
          animation: slideInBottom 0.3s cubic-bezier(0.4, 0, 0.2, 1) both;
        }
        
        @keyframes slideInTop {
          from {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutBottom {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
        }
        
        @keyframes slideInBottom {
          from {
            transform: translateX(-50%) translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes slideOutTop {
          from {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
          to {
            transform: translateX(-50%) translateY(-100%);
            opacity: 0;
          }
        }
      `}</style>

      <div className={`review-benefits-container ${isAnimating ? 'open' : ''} ${reviewSettings?.channel === 'kakao' ? 'kakao-tone' : ''}`}>
        <div className="review-benefits-wrapper">
          <div className="dc-container">
            <div className="dc-top"
              onTouchStart={(e) => {
                carouselStartX.current = e.touches[0].clientX;
                isDraggingCarousel.current = true;
                setDragOffset(0);
              }}
              onTouchMove={(e) => {
                if (!isDraggingCarousel.current) return;
                let diff = e.touches[0].clientX - carouselStartX.current;
                if (Math.abs(diff) > 10) e.preventDefault();

                const currentIdx = benefits.findIndex(b => b.id === selectedBenefit);
                const lastIdx = benefits.length - 1;
                // 양 끝 아이템에서 바깥 방향으로 드래그하면 저항 적용 (화면 밖 이탈 방지)
                if (currentIdx === 0 && diff > 0) {
                  diff = diff * 0.2;
                } else if (currentIdx === lastIdx && diff < 0) {
                  diff = diff * 0.2;
                }
                setDragOffset(diff);

                // 실시간 활성 카드 계산
                const cardWidth = 202; // 186px + 16px gap
                // 감도를 높이기 위해 이동거리를 3.5배로 증폭
                const shifted = currentIdx - (diff * 3.5) / cardWidth;
                const nearest = Math.max(0, Math.min(benefits.length - 1, Math.round(shifted)));
                setPreviewIdx(nearest);
              }}
              onTouchEnd={() => {
                isDraggingCarousel.current = false;
                if (previewIdx !== null && previewIdx !== benefits.findIndex(b => b.id === selectedBenefit)) {
                  handleBenefitSelect(benefits[previewIdx].id);
                }
                setPreviewIdx(null);
                setDragOffset(0);
              }}
            >
              <div className="dc-n-logo dc-enter dc-enter-1">
                {reviewSettings?.channel === 'kakao' ? (
                  <svg viewBox="0 0 450 450" width="40" height="40" xmlns="http://www.w3.org/2000/svg" className="dc-n-logo-img" aria-label="카카오">
                    <path fill="#fae000" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M220.265 496.122c-34.176 0-61.88 27.703-61.88 61.866v312.815c0 34.176 27.704 61.879 61.88 61.879h312.814c34.176 0 61.867-27.703 61.867-61.88V557.989c0-34.163-27.69-61.866-61.867-61.866z" />
                    <path fill="#007dfc" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M378.298 552.754c-63.196-.038-114.244 50.744-114.333 113.586-.05 28.5 11.666 52.834 29.312 73.596 32.187 37.849 60.359 77.712 75.736 125.556 1.381 4.307 3.459 9.222 9.4 9.07 6.46-.165 8.195-5.662 9.728-10.311 14.428-43.778 38.546-81.59 69.01-115.942 18.102-20.407 32.63-43.93 35.063-71.57 5.94-67.338-47.312-124.91-113.916-123.985m38.863 114.662c-.152 20.8-18.456 38.99-39.04 38.8-20.774-.19-38.8-18.444-38.723-39.167.076-20.71 18.392-38.875 39.09-38.774 20.673.089 38.825 18.469 38.673 39.141" />
                  </svg>
                ) : (
                  <img src={reviewIcon} alt="N" className="dc-n-logo-img" />
                )}
              </div>
              <div className="dc-headline dc-enter dc-enter-2">
                {(reviewSettings?.channel === 'kakao' ? '카카오' : '네이버')} 리뷰 작성하면<br />
                <span className="dc-green">
                  {(() => {
                    const selected = benefits.find(b => b.id === selectedBenefit);
                    if (headerMenuAnimating && previousMenuName) {
                      return (
                        <span className="dc-rolling-wrapper">
                          <span className="dc-rolling-exit">{previousMenuName}</span>
                          <span className="dc-rolling-enter">{selected?.text}</span>
                        </span>
                      );
                    }
                    return selected?.text;
                  })()}
                </span>
                {' '}드려요!
              </div>
              <div className="dc-carousel dc-enter dc-enter-3">
                  <div
                    className="dc-carousel-track"
                    style={{
                      transform: `translateX(calc(-${benefits.findIndex(b => b.id === selectedBenefit)} * (186px + 16px) + ${dragOffset}px))`,
                      transition: isDraggingCarousel.current ? 'none' : 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {benefits.map((benefit) => (
                      <div
                        key={benefit.id}
                        className={`dc-reward-card ${(previewIdx !== null ? benefits[previewIdx]?.id === benefit.id : selectedBenefit === benefit.id) ? 'active' : ''}`}
                        onClick={() => handleBenefitSelect(benefit.id)}
                      >
                        <span className="dc-sparkle">&#10022;</span>
                        <span className="dc-sparkle">&#10022;</span>
                        <span className="dc-sparkle">&#10022;</span>
                        <div className="dc-reward-main">
                          {benefit.menuData?.image_url ? (
                            <img className="dc-reward-image" src={benefit.menuData.image_url} alt={benefit.text} />
                          ) : (
                            <div className="dc-reward-no-image">N</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="dc-star-req dc-enter dc-enter-4">리뷰 작성 시 별 5개 필수</div>
                <div className="dc-star-row dc-enter dc-enter-5">★★★★★</div>

            </div>

            <div className="dc-bottom dc-enter dc-enter-6">
              {benefits.length > 1 && (
                <div className="dc-step-dots">
                  {benefits.map((b) => (
                    <div key={b.id} className={`dc-dot ${selectedBenefit === b.id ? 'active' : ''}`} />
                  ))}
                </div>
              )}
              <button className="dc-btn-primary" {...tapClickProps} onClick={handleReviewOrder}>
                리뷰 약속하고, 주문하기
              </button>
              <button className="dc-btn-skip" {...tapClickProps} onClick={handleJustOrder}>
                괜찮아요, 주문만 할래요
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewBenefits;